import React from "react";
import { PropTypes } from "prop-types";
import { observer } from "mobx-react";
import { map, isEmpty, filter } from "lodash";
import { MrbPreviewPage, MrbPageContentHeader } from "mrb/components/layout";
import { MrbContentLoader, MrbHeaderLoader } from "mrb/components/loader";
import { MrbCollapsiblePanel } from "mrb/components/collapsible-panel";
import { MrbButton } from "mrb/components/button";
import { MrbNumericInput } from "mrb/components/input";
import { MrbCheckbox } from "mrb/components/checkbox";
import { NakiNigunArtistAlbumLoader } from "administration/naki-nigun/components";
import { Image } from "common/components";
import { applicationDefaults } from "common/constants";
import { dateFormatter } from "mrb/common";
import { defaultTemplate } from "common/hoc";
import {
    NakiNigunImportProducerEditModalTemplate,
    NakiNigunImportArtworkUploadModalTemplate,
} from "themes/administration/naki-nigun-import/components";

function formatDateTime(value) {
    if (!value) {
        return "—";
    }
    return dateFormatter.format(value, { format: applicationDefaults.formats.dateTime });
}

const KEY = "ADMINISTRATION.NAKI_NIGUN_IMPORT";

function StatusBadge({ status, t }) {
    if (!status) {
        return null;
    }
    const isNegative = status === "Failed";
    const label = t(`${KEY}.PREVIEW.STATUS.${status.toUpperCase()}`);
    return (
        <span className={`c-tag c-tag--sml c-tag--${isNegative ? "inactive" : "active"}--outline`}>
            <i
                className={`u-icon u-icon--med u-icon--circle--${
                    isNegative ? "inactive" : "active"
                } u-mar--right--micro`}
            ></i>
            {label}
        </span>
    );
}

// A producer summary rendered inline. Falls back to a warning when producers are missing/invalid.
const ProducerSummary = observer(function ProducerSummary({ producers, isValid, t }) {
    if (isEmpty(producers)) {
        return (
            <span className="u-type--sml u-type--color--warning">
                <i className="u-icon u-icon--med u-icon--flag u-mar--right--micro"></i>
                {t(`${KEY}.EDIT.PRODUCERS.NONE`)}
            </span>
        );
    }
    return (
        <span
            className={`u-type--sml ${isValid ? "u-type--color--opaque" : "u-type--color--warning"}`}
            title={isValid ? undefined : t(`${KEY}.EDIT.PRODUCERS.INVALID`)}
        >
            {!isValid && <i className="u-icon u-icon--med u-icon--flag u-mar--right--micro"></i>}
            {map(producers, (p) => `${p.name || p.producerId} ${p.revenuePercentage}%`).join(" · ")}
        </span>
    );
});

// A fixed-size, clipped thumbnail box so the shared cover-icon image can't overflow and occlude
// sibling content — the footprint is always exactly `size`px.
function Thumb({ src, size }) {
    const dimension = `${size}px`;
    return (
        <span
            style={{
                display: "inline-block",
                width: dimension,
                height: dimension,
                overflow: "hidden",
                borderRadius: "6px",
                flex: "0 0 auto",
            }}
        >
            {/* No c-home__preview__icon here — its fixed CSS size overrides width/height and forces the
                image to overflow. Plain width/height attrs let the image scale to the box (square covers). */}
            <Image src={src} width={size} height={size} />
        </span>
    );
}

// A compact, clickable artwork thumbnail. When editable, clicking opens the upload modal; a small
// pencil badge signals it's editable (no inline dropzone — keeps rows tight).
const ArtworkThumb = observer(function ArtworkThumb({ store, editable, uploadStore, coverUrl, size, title, t }) {
    if (!editable || !uploadStore) {
        return <Thumb src={coverUrl} size={size} />;
    }
    const dimension = `${size}px`;
    return (
        <div
            className="u-cursor--pointer"
            style={{ position: "relative", width: dimension, height: dimension, flex: "0 0 auto" }}
            title={t(`${KEY}.EDIT.BUTTONS.REPLACE_ARTWORK`)}
            onClick={() => store.openArtworkEditor(uploadStore, title)}
        >
            <Thumb src={coverUrl} size={size} />
            <span
                style={{
                    position: "absolute",
                    right: "-4px",
                    bottom: "-4px",
                    background: "rgba(0,0,0,0.7)",
                    borderRadius: "50%",
                    width: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <i className="u-icon u-icon--sml u-icon--edit"></i>
            </span>
        </div>
    );
});

const ActionBar = observer(function ActionBar({ store, t }) {
    if (store.isEditable) {
        return (
            <div className="u-mar--top--base">
                <div className="u-display--flex u-display--flex--ai--center u-gap--sml u-mar--top--micro">
                    <MrbButton
                        t={t}
                        className="c-btn c-btn--base c-btn--primary"
                        label={t(`${KEY}.EDIT.BUTTONS.SAVE`)}
                        onClick={store.save}
                        disabled={store.isSaving}
                        aria-busy={store.isSaving}
                    />
                    <span
                        className="u-display--flex u-display--flex--ai--center"
                        title={store.canAddToSystem ? undefined : store.incompleteSummary || undefined}
                    >
                        <MrbButton
                            t={t}
                            className="c-btn c-btn--base c-btn--primary"
                            label={t(`${KEY}.EDIT.BUTTONS.ADD_TO_SYSTEM`)}
                            onClick={store.addToSystem}
                            disabled={!store.canAddToSystem || store.isSaving}
                        />
                    </span>
                    <MrbButton
                        t={t}
                        className="c-btn c-btn--base c-btn--ghost"
                        label={t(`${KEY}.EDIT.BUTTONS.DISCARD`)}
                        onClick={store.discard}
                    />
                </div>
            </div>
        );
    }
    if (store.isInSystem) {
        return (
            <div className="u-mar--top--base">
                <MrbButton
                    t={t}
                    className="c-btn c-btn--base c-btn--primary"
                    label={t(`${KEY}.PREVIEW.BUTTONS.ENABLE_ALL`)}
                    onClick={store.enableAll}
                />
            </div>
        );
    }
    return null;
});

// Batch-wide controls above the artist list: assign producers to every album at once (multi-artist
// batches), and a global album-selection mode for assigning to a hand-picked set.
const ReviewToolbar = observer(function ReviewToolbar({ store, t }) {
    if (!store.isEditable) {
        return null;
    }
    const selectMode = store.isSelectMode;
    const selectedCount = store.selectedAlbums.length;
    if (!selectMode) {
        return (
            <div className="u-mar--bottom--lrg">
                <div className="c-import-toolbar__row u-display--flex u-display--flex--ai--center u-gap--sml">
                    {store.hasMultipleArtists && (
                        <MrbButton
                            t={t}
                            classNameOverride="c-btn c-btn--sml c-btn--ghost"
                            label={t(`${KEY}.EDIT.BUTTONS.ASSIGN_ALL_ALBUMS`)}
                            onClick={() => store.editAllAlbumsProducers()}
                        />
                    )}
                    <MrbButton
                        t={t}
                        classNameOverride="c-btn c-btn--sml c-btn--ghost"
                        label={t(`${KEY}.EDIT.BUTTONS.SELECT_ALBUMS`)}
                        onClick={store.toggleSelectMode}
                    />
                </div>
            </div>
        );
    }
    return (
        <div className="u-mar--bottom--lrg">
            <div className="c-import-toolbar__row u-display--flex u-display--flex--ai--center u-display--flex--jc--space-between u-gap--sml">
                <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                    <MrbButton
                        t={t}
                        classNameOverride="c-btn c-btn--sml c-btn--primary"
                        label={t(`${KEY}.EDIT.BUTTONS.ASSIGN_SELECTED`)}
                        onClick={() => store.editSelectedAlbumProducers()}
                        disabled={selectedCount === 0}
                    />
                    <MrbButton
                        t={t}
                        classNameOverride="c-btn c-btn--sml c-btn--ghost"
                        label={t(`${KEY}.EDIT.BUTTONS.CLEAR_SELECTION`)}
                        onClick={store.clearAlbumSelection}
                        disabled={selectedCount === 0}
                    />
                    <span className="u-type--sml u-type--color--opaque">
                        {t(`${KEY}.EDIT.SELECTED_ALBUMS`, { count: selectedCount })}
                    </span>
                </div>
                <MrbButton
                    t={t}
                    classNameOverride="c-btn c-btn--sml c-btn--primary"
                    label={t(`${KEY}.EDIT.BUTTONS.SELECT_DONE`)}
                    onClick={store.toggleSelectMode}
                />
            </div>
        </div>
    );
});

const TrackRow = observer(function TrackRow({ track, album, store, t }) {
    const editable = store.isEditable;
    const overrideSummary = store.trackProducerSummary(track);
    return (
        <div className="c-import-track-row u-display--flex u-display--flex--ai--center u-display--flex--jc--space-between u-gap--med">
            <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                <span className="u-type--sml u-type--color--opaque" style={{ minWidth: "18px" }}>
                    {track.sortOrder || ""}
                </span>
                <ArtworkThumb
                    store={store}
                    editable={editable}
                    uploadStore={editable ? store.trackArtworkStore(track) : null}
                    coverUrl={store.trackCoverUrl(track, album)}
                    size="32"
                    title={track.title}
                    t={t}
                />
                <span className="u-type--sml u-type--ellipsis">{track.title}</span>
            </div>
            <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                {overrideSummary ? (
                    <span className="u-type--sml u-type--color--opaque">
                        {overrideSummary}{" "}
                        <span className="c-tag c-tag--sml c-tag--active--outline u-mar--left--micro">
                            {t(`${KEY}.EDIT.PRODUCERS.OVERRIDDEN`)}
                        </span>
                    </span>
                ) : (
                    <span className="u-type--sml u-type--color--opaque">{t(`${KEY}.EDIT.PRODUCERS.INHERITS_ALBUM`)}</span>
                )}
                {editable && (
                    <MrbButton
                        t={t}
                        classNameOverride="c-btn c-btn--sml c-btn--ghost"
                        label={t(`${KEY}.EDIT.BUTTONS.OVERRIDE`)}
                        onClick={() => store.editTrackProducers(track, album)}
                    />
                )}
                <StatusBadge status={track.status} t={t} />
            </div>
        </div>
    );
});

const AlbumPanel = observer(function AlbumPanel({ album, store, t }) {
    const editable = store.isEditable;
    // Read isSelectMode / selected state / header values in this observer body so the collapsed header —
    // rendered inside the non-observer MrbCollapsiblePanelHeader — refreshes when they change (its reads
    // aren't tracked there).
    const selectMode = editable && store.isSelectMode;
    const isAlbumSelected = selectMode && store.isAlbumSelected(album.id);
    const albumProducers = album.producers;
    const albumValid = store.isAlbumValid(album);
    const albumYear = album.year;
    const albumCoverUrl = store.albumCoverUrl(album);
    return (
        <MrbCollapsiblePanel
            title={album.title}
            t={t}
            isCollapsed={store.isAlbumExpanded(album.id)}
            onChangeCollapse={() => store.toggleAlbum(album.id)}
            containerClassNameExtend="c-import-album"
            headerClassNameExtend="u-cursor--pointer"
            headerRender={({ isCollapsed }) => (
                <React.Fragment>
                    <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                        {selectMode && (
                            <span
                                className="u-display--flex u-display--flex--ai--center"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MrbCheckbox
                                    id={`album-${album.id}`}
                                    classNameExtend="c-import-check"
                                    value={isAlbumSelected}
                                    onChange={() => store.toggleAlbumSelected(album.id)}
                                />
                            </span>
                        )}
                        <Thumb src={albumCoverUrl} size="40" />
                        <span className="u-type--base u-type--wgt--semibold">{album.title}</span>
                        <StatusBadge status={album.status} t={t} />
                    </div>
                    <div className="u-display--flex u-display--flex--ai--center u-gap--base">
                        <ProducerSummary producers={albumProducers} isValid={albumValid} t={t} />
                        {albumYear && <span className="u-type--sml u-type--color--opaque">{albumYear}</span>}
                        <i
                            className={`u-icon u-icon--med u-icon--arrow-down ${
                                isCollapsed ? "u-rotate--180" : ""
                            } u-cursor--pointer`}
                        ></i>
                    </div>
                </React.Fragment>
            )}
        >
            {() => (
                <div className="u-mar--top--sml">
                    {/* Album-level producers + year + artwork */}
                    <div className="u-display--flex u-display--flex--ai--center u-display--flex--jc--space-between u-gap--sml u-mar--bottom--sml">
                        <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                            <span className="u-type--sml u-type--color--opaque">
                                {t(`${KEY}.EDIT.LABELS.ALBUM_PRODUCERS`)}
                            </span>
                            <ProducerSummary producers={albumProducers} isValid={albumValid} t={t} />
                        </div>
                        {editable && (
                            <MrbButton
                                t={t}
                                classNameOverride="c-btn c-btn--sml c-btn--ghost"
                                label={t(`${KEY}.EDIT.BUTTONS.EDIT_PRODUCERS`)}
                                onClick={() => store.editAlbumProducers(album)}
                            />
                        )}
                    </div>

                    {editable && (
                        <div className="u-display--flex u-display--flex--ai--center u-gap--base u-mar--bottom--sml">
                            <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                                <span className="u-type--sml u-type--color--opaque">
                                    {t(`${KEY}.EDIT.LABELS.YEAR`)}
                                </span>
                                <MrbNumericInput
                                    className="c-input c-input--outline c-input--numeric c-input--numeric--sml"
                                    precision={0}
                                    step={1}
                                    t={t}
                                    value={album.year}
                                    min={0}
                                    max={9999}
                                    onChange={(e) => store.setYear(album, e)}
                                    strict={true}
                                />
                            </div>
                            <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                                <span className="u-type--sml u-type--color--opaque">
                                    {t(`${KEY}.EDIT.LABELS.ALBUM_ARTWORK`)}
                                </span>
                                <ArtworkThumb
                                    store={store}
                                    editable
                                    uploadStore={store.albumArtworkStore(album)}
                                    coverUrl={albumCoverUrl}
                                    size="40"
                                    title={album.title}
                                    t={t}
                                />
                            </div>
                        </div>
                    )}

                    <div className="u-separator--primary u-mar--none"></div>

                    {!isEmpty(album.tracks) ? (
                        <div className="u-mar--top--sml">
                            <p className="u-type--sml u-type--color--opaque u-mar--bottom--sml">
                                {t(`${KEY}.PREVIEW.LABELS.TRACKS`)}
                            </p>
                            {map(album.tracks, (track) => (
                                <TrackRow key={track.id} track={track} album={album} store={store} t={t} />
                            ))}
                        </div>
                    ) : null}
                </div>
            )}
        </MrbCollapsiblePanel>
    );
});

// Stands out from the card: an amber callout with an exclamation badge announcing the name match, plus
// the opt-out checkbox. Merge (default) vs. create-new is spelled out so the admin can't miss it.
const MergeCallout = observer(function MergeCallout({ artist, store, t }) {
    const merging = store.isMergingArtist(artist);
    return (
        <div className={`c-import-merge ${merging ? "c-import-merge--merging" : "c-import-merge--new"}`}>
            <span className="c-import-merge__badge" aria-hidden="true">
                !
            </span>
            <div className="c-import-merge__body">
                <p className="c-import-merge__title">{t(`${KEY}.EDIT.MERGE.HEADING`)}</p>
                <p className="c-import-merge__desc">
                    {merging
                        ? t(`${KEY}.EDIT.MERGE.MERGE_DESC`, { name: artist.existingArtistName || artist.name })
                        : t(`${KEY}.EDIT.MERGE.CREATE_DESC`)}
                </p>
                <MrbCheckbox
                    id={`create-new-${artist.id}`}
                    label={`${KEY}.EDIT.MERGE.CREATE_AS_NEW`}
                    value={!!artist.createNewArtist}
                    onChange={() => store.toggleCreateNewArtist(artist)}
                    t={t}
                />
            </div>
        </div>
    );
});

const ArtistCard = observer(function ArtistCard({ artist, store, t }) {
    const editable = store.isEditable;
    const selectMode = editable && store.isSelectMode;
    const hasMatch = store.hasExistingArtistMatch(artist);
    const merging = editable && store.isMergingArtist(artist);
    const isArtistSelected = selectMode && store.isArtistSelected(artist);
    return (
        <div className="c-card c-card--primary u-mar--bottom--med">
            <div className="u-display--flex u-display--flex--ai--center u-display--flex--jc--space-between u-gap--base">
                <div className="u-display--flex u-display--flex--ai--center u-gap--base">
                    {selectMode && !isEmpty(artist.albums) && (
                        <span className="u-display--flex u-display--flex--ai--center">
                            <MrbCheckbox
                                id={`artist-${artist.id}`}
                                classNameExtend="c-import-check"
                                value={isArtistSelected}
                                onChange={() => store.toggleArtistSelected(artist)}
                            />
                        </span>
                    )}
                    <ArtworkThumb
                        store={store}
                        editable={editable && !merging}
                        uploadStore={editable && !merging ? store.artistArtworkStore(artist) : null}
                        coverUrl={store.artistCoverUrl(artist)}
                        size="64"
                        title={artist.name}
                        t={t}
                    />
                    <div>
                        <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                            <span className="u-type--med u-type--wgt--bold">{artist.name}</span>
                            <StatusBadge status={artist.status} t={t} />
                        </div>
                        {!isEmpty(artist.albums) && (
                            <p className="u-type--sml u-type--color--opaque u-mar--top--tny">
                                {artist.albums.length} {t(`${KEY}.PREVIEW.LABELS.ALBUMS`)}
                            </p>
                        )}
                    </div>
                </div>
                {editable && !isEmpty(artist.albums) && (
                    <div className="u-display--flex u-display--flex--ai--center u-gap--sml">
                        <MrbButton
                            t={t}
                            classNameOverride="c-btn c-btn--sml c-btn--ghost"
                            label={t(`${KEY}.EDIT.BUTTONS.SET_ALL_ALBUM_PRODUCERS`)}
                            onClick={() => store.editArtistProducers(artist)}
                        />
                    </div>
                )}
            </div>

            {editable && hasMatch && <MergeCallout artist={artist} store={store} t={t} />}

            {artist.status === "Failed" && artist.message && (
                <p className="c-validation__label u-mar--top--sml">{artist.message}</p>
            )}

            {!isEmpty(artist.albums) && (
                <React.Fragment>
                    <div className="u-separator--primary"></div>
                    {map(artist.albums, (album) => (
                        <AlbumPanel key={album.id} album={album} store={store} t={t} />
                    ))}
                </React.Fragment>
            )}
        </div>
    );
});

// Read-only, at-a-glance status panel: each artist with album/track counts, a merge/new tag, and the
// albums still missing valid producers. Sits to the right of the list (on top on narrow screens).
const ReviewSummary = observer(function ReviewSummary({ store, t }) {
    const summary = store.reviewSummary;
    if (isEmpty(summary)) {
        return null;
    }
    return (
        <div className="c-import-summary">
            <p className="c-import-summary__title">{t(`${KEY}.EDIT.REVIEW_SUMMARY.TITLE`)}</p>
            {map(summary, (artist) => {
                const missingAlbums = filter(artist.albums, (album) => !album.valid);
                return (
                    <div key={artist.id} className="c-import-summary__artist">
                        <div className="c-import-summary__head">
                            <span className="c-import-summary__name u-type--ellipsis">{artist.name}</span>
                            {artist.merging ? (
                                <span className="c-tag c-tag--sml c-tag--inactive--outline">
                                    {t(`${KEY}.EDIT.REVIEW_SUMMARY.MERGE`)}
                                </span>
                            ) : null}
                        </div>
                        <div className="c-import-summary__meta">
                            {isEmpty(artist.albums)
                                ? t(`${KEY}.EDIT.REVIEW_SUMMARY.NO_ALBUMS`)
                                : `${t(`${KEY}.EDIT.REVIEW_SUMMARY.ALBUMS`, { count: artist.albumCount })} · ${t(
                                      `${KEY}.EDIT.REVIEW_SUMMARY.TRACKS`,
                                      { count: artist.trackCount }
                                  )}`}
                        </div>
                        {!isEmpty(artist.albums) &&
                            (artist.missingCount > 0 ? (
                                <div className="c-import-summary__status c-import-summary__status--warning">
                                    <i className="u-icon u-icon--med u-icon--flag u-mar--right--micro"></i>
                                    {t(`${KEY}.EDIT.REVIEW_SUMMARY.NEEDS_PRODUCERS`, { count: artist.missingCount })}
                                    <ul className="c-import-summary__albums">
                                        {map(missingAlbums, (album) => (
                                            <li key={album.id} className="u-type--ellipsis">
                                                {album.title}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className="c-import-summary__status c-import-summary__status--ready">
                                    {t(`${KEY}.EDIT.REVIEW_SUMMARY.ALL_READY`)}
                                </div>
                            ))}
                    </div>
                );
            })}
        </div>
    );
});

function NakiNigunImportBatchPreviewTemplate({ nakiNigunImportBatchPreviewViewStore, t }) {
    const store = nakiNigunImportBatchPreviewViewStore;
    const { batch, loaderStore } = store;

    return (
        <MrbPreviewPage store={store} contentClassNameExtend="u-padd--none" t={t}>
            <MrbPageContentHeader classNameOverride="c-content__header--primary">
                <MrbContentLoader loaderStore={loaderStore} loaderRender={() => <MrbHeaderLoader />}>
                    {() =>
                        batch ? (
                            <div className="c-content__header--primary__info u-animation--fadeIn">
                                <div className="u-display--flex u-display--flex--ai--center u-gap--base u-mar--bottom--sml">
                                    <p className="c-content__header--primary__title">{t(`${KEY}.PREVIEW.TITLE`)}</p>
                                    <StatusBadge status={batch.status} t={t} />
                                </div>
                                <div className="u-display--flex u-display--flex--ai--center u-gap--lrg u-type--sml u-type--color--opaque">
                                    <span>
                                        {t(`${KEY}.PREVIEW.SUMMARY.STARTED`)}: {formatDateTime(batch.startedAt)}
                                    </span>
                                    <span>
                                        {t(`${KEY}.PREVIEW.SUMMARY.COMPLETED`)}: {formatDateTime(batch.completedAt)}
                                    </span>
                                    {batch.enabledAt && (
                                        <span>
                                            {t(`${KEY}.PREVIEW.SUMMARY.ENABLED`)}: {formatDateTime(batch.enabledAt)}
                                        </span>
                                    )}
                                </div>
                                <ActionBar store={store} t={t} />
                            </div>
                        ) : null
                    }
                </MrbContentLoader>
            </MrbPageContentHeader>

            <div className="c-container c-container--lrg">
                <MrbContentLoader loaderStore={loaderStore} loaderRender={() => <NakiNigunArtistAlbumLoader />}>
                    {() =>
                        batch && !isEmpty(batch.artists) ? (
                            <div className="c-import-review u-animation--fadeIn">
                                <div className="c-import-review__main">
                                    <ReviewToolbar store={store} t={t} />
                                    {map(batch.artists, (artist) => (
                                        <ArtistCard key={artist.id} artist={artist} store={store} t={t} />
                                    ))}
                                </div>
                                <aside className="c-import-review__summary">
                                    <ReviewSummary store={store} t={t} />
                                </aside>
                            </div>
                        ) : null
                    }
                </MrbContentLoader>
            </div>

            <NakiNigunImportProducerEditModalTemplate modalParams={store.producerEditModal} t={t} />
            <NakiNigunImportArtworkUploadModalTemplate modalParams={store.artworkModal} t={t} />
        </MrbPreviewPage>
    );
}

NakiNigunImportBatchPreviewTemplate.propTypes = {
    nakiNigunImportBatchPreviewViewStore: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

export default defaultTemplate(NakiNigunImportBatchPreviewTemplate);
